import React, { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import * as Icons from "lucide-react";
import { FEATURES } from "../../data/content";
import { Reveal, Eyebrow } from "./Reveal";

const Counter = ({ value, suffix, plain }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView || plain) return;
    let raf;
    const start = performance.now();
    const dur = 1400;
    const tick = (now) => {
      const t = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setN(Math.round(eased * value));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, plain]);

  return (
    <span ref={ref} className="font-heading font-extrabold text-4xl md:text-5xl text-white leading-none">
      {plain ? "$0" : n}
      <span className="text-[#F56FB4]">{suffix}</span>
    </span>
  );
};

export const WhyChoose = () => (
  <section id="why" className="relative py-24 md:py-32 bg-[#111111]">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <div>
          <Reveal><Eyebrow>Why Choose Lotus</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-heading font-extrabold uppercase text-white text-4xl md:text-5xl lg:text-6xl tracking-tight mt-3">
              Built on <span className="text-[#F56FB4]">Trust</span>
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <p className="text-white/60 max-w-sm">A crew you can rely on, pricing you can trust, and results that speak for themselves.</p>
        </Reveal>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
        {FEATURES.map((f, i) => {
          const Icon = Icons[f.icon] || Icons.Star;
          const accent = i % 2 === 0 ? "#F56FB4" : "#53C653";
          return (
            <Reveal key={f.title} delay={(i % 4) * 0.07}>
              <div className="group h-full rounded-3xl border border-white/10 bg-[#161616]/70 backdrop-blur-xl p-6 md:p-7 transition-transform duration-300 hover:-translate-y-1.5">
                <Icon size={26} style={{ color: accent }} className="mb-4" />
                <Counter value={f.value} suffix={f.suffix} plain={f.plain} />
                <h3 className="font-heading font-bold text-lg text-white mt-3 uppercase tracking-tight">{f.title}</h3>
                <p className="text-xs text-white/50 mt-1">{f.label}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export default WhyChoose;
